import { Send, StopRounded } from "@mui/icons-material"
import { IconButton, TextField } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useAnalytics } from "connected-components/analytics"
import { isEnterKey } from "helpers/keyboard"
import { AI_TEAMMATE_EVENTS } from "../../analytics/events"
import { useMessagesContext, useThreadsContext } from "../../hooks/context"
import { useShouldDisableUserInput } from "../../hooks/selectors"
import "./style.scss"

function AiTeammateMessageInput() {
  const inputRef = useRef(null)
  const messageSourceRef = useRef({ source: "typed" })
  const { isThreadOpen } = useThreadsContext()
  const { sendMessage, registerUserMessageInputOverrideCallback, registerUserMessageInputFocusCallback, isSendingMessage, stopSendMessage } = useMessagesContext()
  const isUserInputDisabled = useShouldDisableUserInput()
  const analytics = useAnalytics()
  const [message, setMessage] = useState("")

  useEffect(() => {
    return registerUserMessageInputOverrideCallback("ai-teammate-message-input", (text, sourceMeta = { source: "typed" }) => {
      messageSourceRef.current = sourceMeta
      setMessage(text)
    })
  }, [registerUserMessageInputOverrideCallback])

  useEffect(() => {
    return registerUserMessageInputFocusCallback("ai-teammate-message-input", () => {
      inputRef.current?.focus()
    })
  }, [registerUserMessageInputFocusCallback])

  useEffect(() => {
    if (isThreadOpen && inputRef.current && !isUserInputDisabled) {
      inputRef.current.focus()
    }
  }, [isUserInputDisabled, isThreadOpen])

  const canSendMessage = !!message.trim() && !isUserInputDisabled

  function handleChange(e) {
    messageSourceRef.current = { source: "typed" }
    setMessage(e.target.value)
  }

  function handleKeyDown(e) {
    if (isEnterKey(e) && (e.metaKey || e.ctrlKey)) {
      handleSendMessage()
    }
  }

  function handleSendMessage() {
    if (canSendMessage) {
      analytics.track(AI_TEAMMATE_EVENTS.CHAT_MESSAGE_SENT, messageSourceRef.current)
      messageSourceRef.current = { source: "typed" }
      sendMessage(message)
      setMessage("")
    }
  }

  return <div className="message-input">
    <div className="container" onClick={() => inputRef.current?.focus()}>
      <TextField
        inputRef={inputRef}
        autoFocus
        disabled={isUserInputDisabled}
        variant="standard"
        fullWidth
        multiline
        placeholder="Ask about your data (e.g. 'What are my position sizes?')"
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            sx: {
              padding: 0,
            },
            disableUnderline: true,
            "aria-label": "Message input",
          },
        }}
      />
      <div className="actions">
        CTRL + Enter to send
        {isSendingMessage && stopSendMessage
          ? <IconButton title="Stop" onClick={stopSendMessage}><StopRounded fontSize="small" /></IconButton>
          : <IconButton title="Send" disabled={!canSendMessage} onClick={handleSendMessage}><Send fontSize="small" /></IconButton>}
      </div>
    </div>
  </div>
}

export default AiTeammateMessageInput
